import type { SubscriptionStatus } from '~/services/subscription/getSubscriptionStatus';
import { isCancelScheduled } from './keepOffer';

/** Rubriques de Mon compte, dans l'ordre du menu. L'URL porte la rubrique ouverte : `?tab=<id>`. */
export const ACCOUNT_SECTIONS = ['subscription', 'venue', 'brand', 'referral', 'profile'] as const;
export type AccountSection = (typeof ACCOUNT_SECTIONS)[number];

export const isAccountSection = (value: unknown): value is AccountSection =>
    ACCOUNT_SECTIONS.includes(value as AccountSection);

/** `?tab=` de la liste des rubriques (téléphone) : l'emporte sur la rubrique d'un lien. */
export const ACCOUNT_MENU_TAB = 'menu';

/** Ids du motif ARIA « tabs » (onglet du menu ↔ panneau de la rubrique). */
export const accountTabId = (id: AccountSection) => `account-tab-${id}`;
export const accountPanelId = (id: AccountSection) => `account-panel-${id}`;

/** Rubrique visée par un lien d'email ou un retour du portail Stripe (route `/account/<…>`). */
export type AccountFocus = 'keep' | 'annual' | 'brand' | null;

/**
 * Rubrique ouverte, par ordre de priorité :
 * 1. `?tab=` s'il désigne une rubrique proposée à ce compte. Pendant la lecture de
 *    l'abonnement (`loading`), une rubrique connue est gardée même si elle n'est pas
 *    encore proposée : Ma marque et Parrainage n'apparaissent qu'une fois le statut lu ;
 * 2. la rubrique d'un lien : « Garder Unboared » / « Passer à l'annuel » → Abonnement,
 *    retour de « Passer en Pro » → Ma marque, lien de parrainage d'un email
 *    (`utm_campaign` contenant « parrainage ») → Parrainage ;
 * 3. sinon aucune n'est choisie (`atMenu`) : le téléphone montre la liste des
 *    rubriques, l'ordinateur ouvre la première.
 * `?tab=menu` (« Retour » du téléphone) : la liste, même arrivé par un lien.
 */
export function resolveAccountSection({
    available,
    tab,
    focus = null,
    campaign = null,
    loading = false,
}: {
    available: AccountSection[];
    tab: string | null;
    focus?: AccountFocus;
    campaign?: string | null;
    loading?: boolean;
}): { section: AccountSection; atMenu: boolean } {
    const offered = (id: AccountSection | null): id is AccountSection => !!id && (available.includes(id) || loading);
    let fromLink: AccountSection | null = null;
    if (focus === 'keep' || focus === 'annual') fromLink = 'subscription';
    else if (focus === 'brand') fromLink = 'brand';
    else if (campaign?.toLowerCase().includes('parrainage')) fromLink = 'referral';

    if (tab === ACCOUNT_MENU_TAB) fromLink = null;
    const requested = isAccountSection(tab) ? tab : null;
    const chosen = [requested, fromLink].find(offered) ?? null;
    return { section: chosen ?? available[0] ?? 'profile', atMenu: !chosen };
}

/** Ligne d'état de l'abonnement sous « Abonnement » dans le menu (clé `account.tabs.status.*`). */
export type SubscriptionSummary =
    | { key: 'trial'; count: number }
    | { key: 'monthly' | 'pro'; amountCents: number; currency: string }
    | { key: 'trialEnding' | 'active' | 'proActive' | 'yearly' | 'proYearly' | 'pastDue' | 'canceled' }
    | { key: 'paused' | 'pauseScheduled' | 'stopping'; date: number };

/** `null` : rien de sûr à dire (statut illisible ou inattendu) → le menu garde sa description fixe. */
export function subscriptionSummary(
    sub: SubscriptionStatus | null,
    { isPro, nowMs = Date.now() }: { isPro: boolean; nowMs?: number }
): SubscriptionSummary | null {
    if (!sub) return null;
    // Pause dont la date de reprise est passée : le statut normal reprend la main.
    const pause = sub.pause?.status === 'paused' && (sub.pause.resumesAt ?? 0) * 1000 > nowMs ? sub.pause : null;
    if (pause?.startsAt && pause.startsAt * 1000 > nowMs) return { key: 'pauseScheduled', date: pause.startsAt };
    if (pause?.resumesAt) return { key: 'paused', date: pause.resumesAt };
    if (sub.status === 'trialing' || sub.status === 'active') {
        const end = sub.cancelAt || (sub.status === 'trialing' ? sub.trialEnd : sub.currentPeriodEnd);
        if (isCancelScheduled(sub)) return end ? { key: 'stopping', date: end } : null;
        if (sub.status === 'trialing') {
            if (!sub.trialEnd) return null;
            const count = Math.ceil((sub.trialEnd * 1000 - nowMs) / 86400000);
            return count > 0 ? { key: 'trial', count } : { key: 'trialEnding' };
        }
        if (sub.interval === 'year') return { key: isPro ? 'proYearly' : 'yearly' };
        // Prix affiché seulement s'il vaut pour un mois normal (mêmes garde-fous que l'offre
        // Pro) : pas de « 0 € » (mois offert), ni de prix remisé ou trimestriel « par mois ».
        const monthlyPrice =
            sub.interval === 'month' &&
            (sub.intervalCount == null || sub.intervalCount === 1) &&
            !sub.discounted &&
            typeof sub.nextAmount === 'number' &&
            sub.nextAmount > 0 &&
            !!sub.currency;
        if (!monthlyPrice) return { key: isPro ? 'proActive' : 'active' };
        return {
            key: isPro ? 'pro' : 'monthly',
            amountCents: sub.nextAmount as number,
            currency: sub.currency as string,
        };
    }
    if (sub.status === 'past_due' || sub.status === 'unpaid') return { key: 'pastDue' };
    if (sub.status === 'canceled' || sub.status === 'incomplete_expired') return { key: 'canceled' };
    return null;
}
